/**
 * Areas API — all backend calls for the Areas tab.
 *
 * Each function maps directly to a V1 endpoint / stored procedure
 * but uses the new V2 api client for cleaner code.
 *
 * Endpoints & payloads are identical to V1 for full backend compatibility.
 */

import { apiFetch, apiTransaction } from '../../../../services/api.js';

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/**
 * Fetch all areas (with devices + groups) for a campus.
 * V1 procedure: sp_dev_getdeviceandgroupforareabycampus
 *
 * @param {number} campusId
 * @param {boolean} [readOnly=true] — false on first load to bust cache
 * @returns {Promise<Array>}
 */
export function fetchAreas(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'sp_dev_getdeviceandgroupforareabycampus',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly,
  });
}

/**
 * Fetch area type options.
 * V1 table: att_area_type (fetchall)
 *
 * @returns {Promise<Array>}
 */
export function fetchAreaTypes() {
  return apiFetch({
    procedure: 'fetchall',
    reqType: 'direct',
    table: 'att_area_type',
    readOnly: true,
  });
}

/**
 * Fetch available devices for assignment (Inovonics devices, all area-compatible categories).
 * V1 procedure: sp_dev_getInovonicsDevices
 * Note: categories include 1-12,15,16,17 (broader than resident device list)
 *
 * @param {number} campusId
 * @returns {Promise<Array>}
 */
export function fetchDevices(campusId) {
  return apiFetch({
    procedure: 'sp_dev_getInovonicsDevices',
    reqType: 'stored',
    payload: {
      device_filter: '',
      device_category_filter: '1,3,4,5,6,8,9,10,11,12,15,16,17',
      campus_id: campusId,
    },
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/**
 * Add a single area.
 * V1 TransType: area_add
 *
 * @param {object} data — Area form data
 * @param {object} session — { campusId, userId, clientId, offsite, offsiteId }
 * @returns {Promise<[boolean, string?]>}
 */
export function addArea(data, session) {
  const payload = [{
    area_name: data.area_name,
    campus_id: session.campusId,
    max_occupancy: parseInt(data.max_occupancy),
    area_type_id: parseInt(data.area_type_id),
    area_group_ids: data.area_group_ids || [],
    building_id: parseInt(data.building_id),
    offsite: session.offsite,
    offsite_id: session.offsiteId,
  }];

  return apiTransaction({
    transType: 'area_add',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Add areas in bulk (name + number range pattern).
 * V1 TransType: area_add (with array payload)
 * V1 limit: 500 areas per bulk add
 *
 * @param {object} params — { baseName, startNumber, endNumber, max_occupancy, area_type_id, area_group_ids, building_id }
 * @param {object} session — { campusId, userId, clientId, offsite, offsiteId }
 * @returns {Promise<[boolean, string?]>}
 */
export function addBulkAreas(params, session) {
  const start = parseInt(params.startNumber);
  const end = parseInt(params.endNumber);
  const payload = [];

  for (let i = start; i <= end; i++) {
    payload.push({
      area_name: `${params.baseName}${i}`,
      campus_id: session.campusId,
      max_occupancy: parseInt(params.max_occupancy),
      area_type_id: parseInt(params.area_type_id),
      area_group_ids: params.area_group_ids || [],
      building_id: parseInt(params.building_id),
      offsite: session.offsite,
      offsite_id: session.offsiteId,
    });
  }

  return apiTransaction({
    transType: 'area_add',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Edit an existing area.
 * V1 TransType: area_edit
 *
 * @param {object} data — Updated area data
 * @param {object} oldData — Original area data (for change tracking)
 * @param {object} session
 * @returns {Promise<[boolean, string?]>}
 */
export function editArea(data, oldData, session) {
  const payload = [{
    area_id: data.area_id,
    area_name: data.area_name,
    max_occupancy: parseInt(data.max_occupancy),
    building_id: parseInt(data.building_id),
    area_type_id: parseInt(data.area_type_id),
    area_group_ids: data.area_group_ids || [],
    campus_id: session.campusId,
    offsite: session.offsite,
    offsite_id: session.offsiteId,
  }];

  const oldPayload = [{
    area_id: oldData.area_id,
    area_name: oldData.area_name,
    max_occupancy: oldData.max_occupancy,
    building_id: oldData.building_id,
    area_type_id: oldData.area_type_id,
    area_group_ids: oldData.area_group_ids || [],
    campus_id: oldData.campus_id || session.campusId,
  }];

  return apiTransaction({
    transType: 'area_edit',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Delete one or more areas.
 * V1 TransType: area_delete
 * Note: caller should unassign devices from the areas first (via unassignDeviceFromArea).
 *
 * @param {Array<number>} areaIds
 * @param {object} session
 * @returns {Promise<[boolean, string?]>}
 */
export function deleteAreas(areaIds, session) {
  const payload = areaIds.map((id) => ({
    area_id: id,
    offsite: session.offsite,
    offsite_id: session.offsiteId,
  }));

  return apiTransaction({
    transType: 'area_delete',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

// ---------------------------------------------------------------------------
// DEVICE operations
// ---------------------------------------------------------------------------

/**
 * Assign an existing device to an area.
 * V1 TransType: device_assign_area
 *
 * @param {object} device — Full device record from fetchDevices
 * @param {number} areaId — The area to assign to
 * @param {object} session — { campusId, userId, clientId, offsite, offsiteId }
 * @returns {Promise<[boolean, string?]>}
 */
export function assignDeviceToArea(device, areaId, areaName, session, grandstream = false) {
  const payload = [{
    device_id: device.device_id,
    campus_id: session.campusId,
    is_active: true,
    area_id: parseInt(areaId),
    device_name: device.device_name,
    manufacture_model_id: parseInt(device.manufacture_model_id),
    manufacture_id: device.manufacture_id,
    sip_extension: device.sip_extension,
    is_grandstream: grandstream,
    device_use_id: parseInt(device.device_use_id),
    descriptions: device.descriptions || '',
    area_name: areaName,
    is_resident: false,
    is_area_device: true,
    device_type_id: device.device_type_id,
    modified_by: session.userId,
    offsite: session.offsite,
    offsite_id: session.offsiteId,
  }];

  const oldPayload = [{
    device_id: device.device_id,
    campus_id: device.campus_id,
    is_active: device.is_active,
    area_id: device.area_id,
    device_use_id: device.device_use_id,
    device_name: device.device_name,
    descriptions: device.descriptions,
    manufacture_model_id: device.manufacture_model_id,
    device_type_id: device.device_type_id,
  }];

  return apiTransaction({
    transType: 'device_assign_area',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Unassign (remove) one or more devices from an area.
 * V1 TransType: device_unassign_area
 *
 * @param {Array<object>} devices — array of device objects with { device_id, device_name }
 * @param {number} areaId — The area they are currently assigned to
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function unassignDevicesFromArea(devices, areaId, session, grandstream = false) {
  const payload = devices.map((d) => ({
    device_id: d.device_id,
    device_name: d.device_name ?? null,
    manufacture_id: d.manufacture_id ?? 0,
    manufacture_uuid: d.manufacture_uuid ?? "",
    sip_extension: d.sip_extension ?? "",
    is_grandstream: grandstream,
    is_active: false,
    area_id: null,
    modified_by: session.userId,
  }));

  const oldPayload = devices.map((d) => ({
    device_id: d.device_id,
    area_id: areaId,
  }));

  return apiTransaction({
    transType: 'device_unassign_area',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}
